/**
 * shopifyApi.eager.ts — functions and types used in the eager bundle.
 *
 * Imported directly by Header, MegaMenu, CartContext (type-only) and any
 * other eagerly-rendered module.  Must stay side-effect-free (no module-level
 * Map/Set initialisation, no class instantiation) so Vite can tree-shake it.
 *
 * Heavy Shopify functions (fetchProductByHandle, createCheckout, customer
 * management, etc.) live in shopifyApi.ts which is lazy-loaded only by pages.
 */

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
import { fetchCatalogResource } from './catalogDataClient';

/* ── Types shared between eager and lazy code ──────────────── */

export interface ShopifyVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  price: { amount: string; currencyCode: string };
}

export interface ShopifySelectedOption {
  name: string;
  value: string;
}

export interface ShopifyImage {
  url: string;
  altText: string | null;
  /** Optional while earlier catalog responses remain cached. */
  width?: number | null;
  height?: number | null;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  tags: string[];
  featuredImage: ShopifyImage | null;
  images: { nodes: ShopifyImage[] };
  priceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
    maxVariantPrice?: { amount: string; currencyCode: string };
  };
  variants: { nodes: ShopifyVariant[] };
  shortDescription?: { value: string } | null;
  /** Shopify collections this product belongs to — used for collection-based filtering */
  collections?: { nodes: Array<{ handle: string; title?: string }> };
}

export interface ShopConfig {
  name: string;
  loginUrl: string;
  accountUrl: string;
  registerUrl: string;
}

export interface ShopifyAccountConfig {
  storeDomain: string;
  publicAccessToken: string;
}

export interface ShopifyCollection {
  title: string;
  description: string;
  image: { url: string; altText: string | null } | null;
  products: {
    nodes: ShopifyProduct[];
    pageInfo?: ShopifyPageInfo;
  };
}

export interface ShopifyCollectionSummary {
  id: string;
  handle: string;
  title: string;
  description: string;
  image: { url: string; altText: string | null } | null;
}

interface ShopifyPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface MenuItem {
  title: string;
  url: string;
  type: string;
  items: Array<{ title: string; url: string; type: string }>;
}

export type OriginKey = 'bukietowa' | 'bursztynowa';

export interface ShippingAddress {
  address1: string;
  city: string;
  zip: string;
  province: string;
  countryCode: string;
}

export interface DeliveryDetails {
  destination: string;
  noStreet?: boolean;        // address has no street name; destination starts with house number
  destinationLatitude?: number;
  destinationLongitude?: number;
  apartmentNumber: string;   // '' when noApartment is true
  noApartment: boolean;      // checkbox: address has no apartment/unit number
  courierNote: string;       // e.g. door code, gate instructions
  origin: OriginKey;
  date: string;          // YYYY-MM-DD
  slot: string;          // e.g. '9-12', '12-15', '15-19', '19-21'
  recipientName: string;
  recipientPhone: string;
  placeType?: string;    // human-readable label of the delivery place type
}

export interface DeliveryCostResult {
  cost: number;
  distanceKm: number;
  breakdown: { base: number; extraKm: number; sunday: number; lateSlot: number };
  shippingAddress: ShippingAddress;
  deliveryAvailable?: boolean;
}

/* ── Eager API functions ────────────────────────────────────── */

export async function fetchShopConfig(): Promise<ShopConfig> {
  const res = await fetch(`${BASE}/api/shopify/shop`);
  if (!res.ok) throw new Error("Failed to fetch shop config");
  return res.json() as Promise<ShopConfig>;
}

export async function fetchShopifyAccountConfig(): Promise<ShopifyAccountConfig> {
  const res = await fetch(`${BASE}/api/shopify/account-config`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error("Failed to fetch Shopify account config");
  return res.json() as Promise<ShopifyAccountConfig>;
}

export async function fetchProducts(limit = 12): Promise<ShopifyProduct[]> {
  const data = await fetchCatalogResource<{ products: ShopifyProduct[] }>(
    `products:${limit}`,
    `${BASE}/api/shopify/products?limit=${limit}`,
  );
  return data.products;
}

/**
 * Retrieves every product published to the Shopify sales channel. Pagination
 * stays server-driven so a larger catalog does not disappear after 250 items.
 */
export async function fetchAllProducts(): Promise<ShopifyProduct[]> {
  const products: ShopifyProduct[] = [];
  let after: string | null = null;

  for (let page = 0; page < 100; page += 1) {
    const params = new URLSearchParams({ limit: "250" });
    if (after) params.set("after", after);
    const data = await fetchCatalogResource<{
      products: ShopifyProduct[];
      pageInfo?: ShopifyPageInfo;
    }>(
      `products-page:${params.toString()}`,
      `${BASE}/api/shopify/products?${params}`,
    );
    products.push(...data.products);

    const nextCursor = data.pageInfo?.endCursor ?? null;
    if (!data.pageInfo?.hasNextPage || !nextCursor || nextCursor === after) break;
    after = nextCursor;
  }

  return products;
}

/** Retrieves every customer-visible Shopify collection with cursor pagination. */
export async function fetchAllCollections(): Promise<ShopifyCollectionSummary[]> {
  const collections: ShopifyCollectionSummary[] = [];
  let after: string | null = null;

  for (let page = 0; page < 100; page += 1) {
    const params = new URLSearchParams({ limit: "250" });
    if (after) params.set("after", after);
    const data = await fetchCatalogResource<{
      collections: ShopifyCollectionSummary[];
      pageInfo?: ShopifyPageInfo;
    }>(
      `collections-page:${params.toString()}`,
      `${BASE}/api/shopify/collections?${params}`,
    );
    collections.push(...data.collections);

    const nextCursor = data.pageInfo?.endCursor ?? null;
    if (!data.pageInfo?.hasNextPage || !nextCursor || nextCursor === after) break;
    after = nextCursor;
  }

  return collections;
}

export async function fetchCollection(
  handle: string,
  { limit = 50, sort = 'COLLECTION_DEFAULT', reverse = false } = {},
): Promise<ShopifyCollection | null> {
  const params = new URLSearchParams({ limit: String(limit), sort, reverse: String(reverse) });
  const data = await fetchCatalogResource<{ collection: ShopifyCollection } | null>(
    `collection:${handle}:${params.toString()}`,
    `${BASE}/api/shopify/collections/${encodeURIComponent(handle)}?${params}`,
    { onNotFound: () => null },
  );
  return data?.collection ?? null;
}

/** Retrieves every product in a Shopify collection using cursor pagination. */
export async function fetchAllCollectionProducts(
  handle: string,
  { sort = 'COLLECTION_DEFAULT', reverse = false } = {},
): Promise<ShopifyProduct[]> {
  const products: ShopifyProduct[] = [];
  let after: string | null = null;

  for (let page = 0; page < 100; page += 1) {
    const params = new URLSearchParams({
      limit: "100",
      sort,
      reverse: String(reverse),
    });
    if (after) params.set("after", after);

    const data = await fetchCatalogResource<{
      collection: ShopifyCollection;
    } | null>(
      `collection-page:${handle}:${params.toString()}`,
      `${BASE}/api/shopify/collections/${encodeURIComponent(handle)}?${params}`,
      { onNotFound: () => null },
    );
    if (!data) return [];
    const pageProducts = data.collection?.products?.nodes ?? [];
    products.push(...pageProducts);

    const nextCursor = data.collection?.products?.pageInfo?.endCursor ?? null;
    if (
      !data.collection?.products?.pageInfo?.hasNextPage ||
      !nextCursor ||
      nextCursor === after
    ) {
      break;
    }
    after = nextCursor;
  }

  return products;
}

export async function fetchMenu(handle: string): Promise<MenuItem[]> {
  const data = await fetchCatalogResource<{ items: MenuItem[] } | null>(
    `menu:${handle}`,
    `${BASE}/api/shopify/menu/${encodeURIComponent(handle)}`,
    { onNotFound: () => null },
  );
  return data?.items ?? [];
}

export function formatPrice(amount: string, currencyCode: string): string {
  const num = parseFloat(amount);
  if (currencyCode === "PLN") return `${num.toFixed(2).replace(".", ",")} zł`;
  return `${num.toFixed(2)} ${currencyCode}`;
}

/**
 * Sibling of formatPrice() for totals computed in JS as plain numbers (cart/checkout
 * sums, delivery cost) rather than parsed from a Shopify price string. Several call
 * sites (audyt refaktoru 01.09.2026) used `.toFixed(2)` directly and skipped the
 * comma substitution, showing e.g. "12.50 zł" to Polish customers instead of "12,50 zł".
 */
export function formatPlnNumber(amount: number): string {
  return `${amount.toFixed(2).replace(".", ",")} zł`;
}

/** Returns "od X zł" when product has variants at different price points, otherwise "X zł". */
export function productDisplayPrice(priceRange: {
  minVariantPrice: { amount: string; currencyCode: string };
  maxVariantPrice?: { amount: string; currencyCode: string };
}): string {
  const { minVariantPrice, maxVariantPrice } = priceRange;
  const formatted = formatPrice(minVariantPrice.amount, minVariantPrice.currencyCode);
  if (maxVariantPrice && parseFloat(maxVariantPrice.amount) > parseFloat(minVariantPrice.amount)) {
    return `od ${formatted}`;
  }
  return formatted;
}
